"use client"

import { ShieldCheck } from "lucide-react"
import { useTranslations } from "next-intl"

import type { EuRepresentative } from "@/lib/storefront/product/product-safety"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/components/sheet"

interface Props {
  productTitle: string
  representative?: EuRepresentative
  safetyHint: string
  isoLocale: string
}

export default function ProductDetailSafetyInformationClient({
  productTitle,
  representative,
  safetyHint,
  isoLocale,
}: Props) {
  const t = useTranslations("storefront.product.safety")
  let country = representative?.country
  if (country && /^[a-z]{2}$/i.test(country)) {
    country =
      new Intl.DisplayNames([isoLocale.replaceAll("_", "-")], { type: "region" }).of(country.toUpperCase()) ?? country
  }
  const fields = representative
    ? [
        { key: "email", value: representative.email },
        { key: "country", value: country },
        { key: "name", value: representative.name },
        { key: "city", value: representative.city },
        { key: "postalCode", value: representative.postalCode },
        { key: "street", value: representative.street },
      ].filter((field) => field.value?.trim())
    : []

  if (!fields.length && !safetyHint) return null

  return (
    <Sheet>
      <div className="border-t border-gray-200 pt-4">
        <SheetTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-sm p-2 text-left text-sm text-primary underline underline-offset-4 focus-visible:outline-2"
          >
            <ShieldCheck className="size-5 shrink-0" aria-hidden="true" />
            {t("link")}
          </button>
        </SheetTrigger>
      </div>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="shrink-0 border-b border-gray-200 p-6 pr-12 text-left">
          <SheetTitle>{t("title")}</SheetTitle>
          <SheetDescription>{productTitle}</SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-col gap-6 overflow-y-auto p-6">
          {fields.length > 0 && (
            <section className="shrink-0 overflow-hidden rounded-sm border border-gray-200">
              <h3 className="bg-gray-100 p-4 font-bold">{t("general")}</h3>
              <dl className="flex flex-col gap-6 p-4">
                {fields.map(({ key, value }) => (
                  <div key={key} className="flex flex-col gap-1">
                    <dt className="font-bold">{t(key)}</dt>
                    <dd className="break-words">
                      {key === "email" ? (
                        <a className="underline" href={`mailto:${value}`}>
                          {value}
                        </a>
                      ) : (
                        value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
          {safetyHint && (
            <section className="flex flex-col gap-3">
              <h3 className="font-bold">{t("hint")}</h3>
              <p className="whitespace-pre-wrap break-words">{safetyHint}</p>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
